/*
 * @Author: cheng
 * @Date: 2018-10-29 11:43:10
 * @Last Modified by: cheng
 * @Last Modified time: 2019-06-21 10:12:24
 */
import {
  DataQuery,
  ResponseConfig,
  CompositeInherit,
  EntityTag,
  ParameterConfig,
  Attribute,
  TrainingPhraseConfig,
  ValueSuggestionData,
  ValueAssociation,
  ValueCheck,
  CronData,
  ConfirmationInfo,
  OriginKey,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  GenericType,
  ExternalType,
  ServiceSlot,
  StateUpdateInfo,
  FrameMapping,
  DialogAct,
  FrameReturn,
  PostFillAction,
  ReferenceNode,
  AugmentMeta
} from '@interface/index';
import { ModelDeclare, ModelProxy } from '@lib/model-proxy';
import { effects } from './effect';

// interface
export interface StateInfo {
  id: string;
  name: string;
  comment?: string;
  source?: string;
  originKey?: OriginKey;
  structKey?: OriginKey;
  dirty: boolean;
  loading: boolean;
  apiLoading: boolean;
  trainingPhrases: TrainingPhraseConfig[];
  negativePhrases: TrainingPhraseConfig[];
  entailmentPhrases: TrainingPhraseConfig[];
  abortPhrases: TrainingPhraseConfig[];
  abortNegativePhrases: TrainingPhraseConfig[];
  abortEntailmentPhrases: TrainingPhraseConfig[];
  parameters: ParameterConfig[];
  availableIntents: IntentInfo[];
  availableComposites: CompositeInfo[];
  availableEntities: EntityInfo[];
  availableChannels: string[];
  selectedParameters: Attribute[];
  globalSelectedParameters: Attribute[];

  inheritedParameters: Attribute[];
  nestedSlotCustomizations: Attribute[];
  branching: boolean;
  dialogAct?: boolean;
  agentResponses: ResponseConfig[];
  elseResponses: ResponseConfig[];
  webHookUrl: string;

  valueSuggestions: ValueSuggestionData[];
  dataQueries: DataQuery[];
  services: ServiceSlot[];
  entityTags: EntityTag[];
  tab?: string;
  selectedCompositeInherit?: CompositeInherit[];
  genericTypes: GenericType[];
  externalTypes: ExternalType[];
  valueAssociations: ValueAssociation[];
  confirmations: ConfirmationInfo[];
  valueChecks: ValueCheck[];
  stateUpdates: StateUpdateInfo[];
  mappings: FrameMapping[];
  dialogActs: DialogAct[];
  postFillActions: PostFillAction[];
  cronData?: CronData;
  triggers: string[];
  description: string;
  qualification: string;
  externalQualification: boolean;
  triggeredByDU: boolean;
  returnInfo?: FrameReturn;
  booleanGatePrompts?: string[];
  booleanGateExpressionsYes: TrainingPhraseConfig[];
  booleanGateExpressionsNo: TrainingPhraseConfig[];
  enableFallback: boolean;
  fallbackInfo?: AugmentMeta;
  soft: boolean;
  softResponse?: AugmentMeta;
}

export type PayloadInfo = { [K in keyof StateInfo]: StateInfo[K] };

// state
export const defaultState: PayloadInfo = {
  id: '',
  name: '',
  dirty: false,
  loading: false,
  apiLoading: false,

  trainingPhrases: [],
  negativePhrases: [],
  entailmentPhrases: [],

  parameters: [],
  availableIntents: [],
  availableComposites: [],
  availableEntities: [],
  availableChannels: [],
  selectedParameters: [],
  nestedSlotCustomizations: [],
  globalSelectedParameters: [],

  inheritedParameters: [],
  branching: false,
  agentResponses: [],
  elseResponses: [],

  webHookUrl: '',
  dataQueries: [],
  services: [],
  entityTags: [],
  tab: undefined,
  valueSuggestions: [],
  genericTypes: [],
  externalTypes: [],
  valueAssociations: [],
  confirmations: [],
  valueChecks: [],
  stateUpdates: [],
  mappings: [],
  dialogActs: [],
  postFillActions: [],
  triggers: [],
  abortPhrases: [],
  abortNegativePhrases: [],
  abortEntailmentPhrases: [],
  description: '',
  qualification: '',
  externalQualification: false,
  triggeredByDU: true,
  booleanGateExpressionsYes: [],
  booleanGateExpressionsNo: [],
  enableFallback: false,
  soft: false
};

const intent: ModelDeclare = {
  effects,
  namespace: 'intent',
  state: defaultState,
  actions: {
    clear: () => defaultState
  }
};

export const intentModel = new ModelProxy<StateInfo, PayloadInfo>(intent);
